import { useEffect, useState } from 'preact/hooks';
import { Book, Chapter } from '../types';
import { useChapterDetails } from './use-chapter-details';

interface UseChaptersProps {
  initialChapters: Chapter[];
  book: Book;
  initialChapter: Chapter;
  chapterPermalink: string;
}

export const useChapters = ({
  initialChapters,
  book,
  initialChapter,
  chapterPermalink,
}: UseChaptersProps) => {
  // Use the extracted hook for chapter list

  const [chaptersList, setChaptersList] = useState<Chapter[]>(
    initialChapters.map((chapter) => {
      if (chapter.id === initialChapter.id) {
        return {
          ...initialChapter,
          hydrated: true,
        };
      }
      return {
        ...chapter,
        hydrated: false,
      };
    }),
  );

  const [currentChapter, setCurrentChapter] = useState<Chapter | undefined>(
    undefined,
  );
  const [nextChapter, setNextChapter] = useState<Chapter | undefined>(
    undefined,
  );
  const [prevChapter, setPrevChapter] = useState<Chapter | undefined>(
    undefined,
  );

  useEffect(() => {
    if (chaptersList.length > 0) {
      const current = chaptersList.find(
        (c: Chapter) => c.permalink === chapterPermalink,
      );
      setCurrentChapter(current);

      if (current) {
        const currentIndex = chaptersList.findIndex(
          (c: Chapter) => c.id === current.id,
        );
        setNextChapter(chaptersList[currentIndex + 1]);
        setPrevChapter(chaptersList[currentIndex - 1]);
      } else {
        setNextChapter(undefined);
        setPrevChapter(undefined);
      }
    }
  }, [chaptersList, chapterPermalink]);

  // Create queries for chapters that need hydration
  const currentChapterQuery = useChapterDetails(
    currentChapter?.hydrated ? undefined : book,
    currentChapter?.hydrated ? undefined : currentChapter?.id,
  );

  const nextChapterQuery = useChapterDetails(
    nextChapter?.hydrated ? undefined : book,
    nextChapter?.hydrated ? undefined : nextChapter?.id,
  );

  const prevChapterQuery = useChapterDetails(
    prevChapter?.hydrated ? undefined : book,
    prevChapter?.hydrated ? undefined : prevChapter?.id,
  );

  // Hydrate chapters when data is available
  useEffect(() => {
    if (
      currentChapterQuery.data &&
      currentChapter &&
      !currentChapter.hydrated
    ) {
      setChaptersList((prev) =>
        prev.map((c) =>
          c.id === currentChapter.id
            ? { ...currentChapterQuery.data, hydrated: true }
            : c,
        ),
      );
    }
  }, [currentChapterQuery.data, currentChapter]);

  useEffect(() => {
    if (nextChapterQuery.data && nextChapter && !nextChapter.hydrated) {
      setChaptersList((prev) =>
        prev.map((c) =>
          c.id === nextChapter.id
            ? { ...nextChapterQuery.data, hydrated: true }
            : c,
        ),
      );
    }
  }, [nextChapterQuery.data, nextChapter]);

  useEffect(() => {
    if (prevChapterQuery.data && prevChapter && !prevChapter.hydrated) {
      setChaptersList((prev) =>
        prev.map((c) =>
          c.id === prevChapter.id
            ? { ...prevChapterQuery.data, hydrated: true }
            : c,
        ),
      );
    }
  }, [prevChapterQuery.data, prevChapter]);

  return {
    chapters: chaptersList,
    chapter: currentChapter,
    nextChapter,
    prevChapter,
    isLoading: false, // Since we're handling hydration in the useEffect
  };
};
